import React from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend);

interface AllocationChartProps {
  data?: any;
  height?: number;
}

const AllocationChart: React.FC<AllocationChartProps> = ({ data, height = 300 }) => {
  // Mock data for demonstration
  const mockData = {
    labels: ['Bitcoin (BTC)', 'Ethereum (ETH)', 'Cardano (ADA)', 'Solana (SOL)', 'Polygon (MATIC)', 'Cash'],
    datasets: [
      {
        label: 'Portfolio Allocation',
        data: [35, 25, 15, 12, 8, 5],
        backgroundColor: [
          '#F7931A', // Bitcoin orange
          '#627EEA', // Ethereum blue
          '#0033AD', // Cardano blue
          '#9945FF', // Solana purple
          '#8247E5', // Polygon purple
          '#6B7280', // Cash gray
        ],
        borderColor: [
          '#F7931A',
          '#627EEA',
          '#0033AD',
          '#9945FF',
          '#8247E5',
          '#6B7280',
        ],
        borderWidth: 2,
        hoverBorderWidth: 3,
      },
    ],
  };

  // Process actual data if provided and valid, otherwise use mock data
  let chartData = mockData;
  
  if (data && Array.isArray(data) && data.length > 0) {
    try {
      const labels = data.map(item => {
        if (item && item.symbol) {
          return item.symbol;
        }
        return 'Unknown';
      }).filter(label => label !== 'Unknown');

      const values = data.map(item => {
        if (item && typeof item.percentage === 'number') {
          return item.percentage;
        } else if (item && typeof item.value === 'number') {
          return item.value;
        }
        return 0;
      });

      if (labels.length > 0 && values.length > 0) {
        // Generate colors for the data
        const colors = ['#F7931A', '#627EEA', '#0033AD', '#9945FF', '#8247E5', '#6B7280', '#EF4444', '#10B981', '#F59E0B'];
        const backgroundColors = labels.map((_, index) => colors[index % colors.length]);

        chartData = {
          labels,
          datasets: [
            {
              label: 'Portfolio Allocation',
              data: values,
              backgroundColor: backgroundColors,
              borderColor: backgroundColors,
              borderWidth: 2,
              hoverBorderWidth: 3,
            }
          ],
        };
      }
    } catch (error) {
      console.warn('Error processing allocation chart data, using mock data:', error);
      // Fall back to mock data
    }
  }

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function(context: any) {
            const label = context.label || '';
            const value = context.parsed;
            return `${label}: ${value}%`;
          },
        },
      },
    },
    cutout: '60%',
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
      <div className="mb-4">
        <h3 className="text-lg font-medium text-gray-900">Asset Allocation</h3>
        <p className="text-sm text-gray-500">Current portfolio distribution</p>
      </div>
      <div style={{ height: `${height}px` }}>
        <Doughnut data={chartData} options={options} />
      </div>
      
      {/* Summary stats below chart */}
      <div className="mt-4 pt-4 border-t border-gray-200">
        <div className="grid grid-cols-3 gap-4 text-center">
          <div>
            <p className="text-2xl font-bold text-gray-900">6</p>
            <p className="text-sm text-gray-500">Assets</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-green-600">95%</p>
            <p className="text-sm text-gray-500">Invested</p>
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-600">5%</p>
            <p className="text-sm text-gray-500">Cash</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AllocationChart; 